/**
 * Camunda 7.18 History REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/history/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  HistoricProcessInstance,
  HistoricProcessInstanceQuery,
  HistoricActivityInstance,
  HistoricActivityInstanceQuery,
  HistoricTaskInstance,
  HistoricTaskInstanceQuery,
  HistoricVariableInstance,
  HistoricVariableInstanceQuery,
  HistoricIncident,
  HistoricIncidentQuery,
  HistoricJobLog,
  HistoricJobLogQuery,
  HistoricDecisionInstance,
  HistoricDecisionInstanceQuery,
  HistoricBatch,
  CleanableHistoricProcessInstanceReport,
  CountResult,
  SetRemovalTimeToHistoricProcessInstancesDto,
  Batch,
} from '../types';

export class HistoryService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/history';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Historic Process Instances
  // ============================================================================

  /**
   * Get historic process instances
   */
  async getProcessInstances(query?: HistoricProcessInstanceQuery): Promise<HistoricProcessInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricProcessInstance[]>(`${this.basePath}/process-instance${params}`);
  }

  /**
   * Get historic process instances count
   */
  async getProcessInstancesCount(query?: HistoricProcessInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/process-instance/count${params}`);
  }

  /**
   * Query historic process instances (POST)
   */
  async queryProcessInstances(query: HistoricProcessInstanceQuery): Promise<HistoricProcessInstance[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<HistoricProcessInstance[]>(`${this.basePath}/process-instance${params}`, body);
  }

  /**
   * Query historic process instances count (POST)
   */
  async queryProcessInstancesCount(query: HistoricProcessInstanceQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/process-instance/count`, query);
  }

  /**
   * Get historic process instance by ID
   */
  async getProcessInstanceById(id: string): Promise<HistoricProcessInstance> {
    return this.client.get<HistoricProcessInstance>(`${this.basePath}/process-instance/${id}`);
  }

  /**
   * Delete historic process instance
   */
  async deleteProcessInstance(
    id: string,
    options?: { failIfNotExists?: boolean }
  ): Promise<void> {
    const params = options?.failIfNotExists !== undefined
      ? `?failIfNotExists=${options.failIfNotExists}`
      : '';
    await this.client.delete<void>(`${this.basePath}/process-instance/${id}${params}`);
  }

  /**
   * Delete historic process instances async
   */
  async deleteProcessInstancesAsync(dto: {
    historicProcessInstanceIds?: string[];
    historicProcessInstanceQuery?: HistoricProcessInstanceQuery;
    deleteReason?: string;
    failIfNotExists?: boolean;
  }): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/process-instance/delete`, dto);
  }

  /**
   * Set removal time for historic process instances
   */
  async setRemovalTime(dto: SetRemovalTimeToHistoricProcessInstancesDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/process-instance/set-removal-time`, dto);
  }

  /**
   * Get duration report
   */
  async getProcessInstanceDurationReport(options: {
    reportType: 'duration';
    periodUnit: 'month' | 'quarter';
    processDefinitionIdIn?: string[];
    processDefinitionKeyIn?: string[];
    startedBefore?: string;
    startedAfter?: string;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('reportType', options.reportType);
    params.append('periodUnit', options.periodUnit);
    if (options.processDefinitionIdIn) params.append('processDefinitionIdIn', options.processDefinitionIdIn.join(','));
    if (options.processDefinitionKeyIn) params.append('processDefinitionKeyIn', options.processDefinitionKeyIn.join(','));
    if (options.startedBefore) params.append('startedBefore', options.startedBefore);
    if (options.startedAfter) params.append('startedAfter', options.startedAfter);
    return this.client.get<any[]>(`${this.basePath}/process-instance/report?${params.toString()}`);
  }

  // ============================================================================
  // Historic Activity Instances
  // ============================================================================

  /**
   * Get historic activity instances
   */
  async getActivityInstances(query?: HistoricActivityInstanceQuery): Promise<HistoricActivityInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricActivityInstance[]>(`${this.basePath}/activity-instance${params}`);
  }

  /**
   * Get historic activity instances count
   */
  async getActivityInstancesCount(query?: HistoricActivityInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/activity-instance/count${params}`);
  }

  /**
   * Query historic activity instances (POST)
   */
  async queryActivityInstances(query: HistoricActivityInstanceQuery): Promise<HistoricActivityInstance[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<HistoricActivityInstance[]>(`${this.basePath}/activity-instance${params}`, body);
  }

  /**
   * Get historic activity instance by ID
   */
  async getActivityInstanceById(id: string): Promise<HistoricActivityInstance> {
    return this.client.get<HistoricActivityInstance>(`${this.basePath}/activity-instance/${id}`);
  }

  // ============================================================================
  // Historic Task Instances
  // ============================================================================

  /**
   * Get historic task instances
   */
  async getTaskInstances(query?: HistoricTaskInstanceQuery): Promise<HistoricTaskInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricTaskInstance[]>(`${this.basePath}/task${params}`);
  }

  /**
   * Get historic task instances count
   */
  async getTaskInstancesCount(query?: HistoricTaskInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/task/count${params}`);
  }

  /**
   * Query historic task instances (POST)
   */
  async queryTaskInstances(query: HistoricTaskInstanceQuery): Promise<HistoricTaskInstance[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<HistoricTaskInstance[]>(`${this.basePath}/task${params}`, body);
  }

  /**
   * Get task duration report
   */
  async getTaskDurationReport(options: {
    reportType: 'duration';
    periodUnit: 'month' | 'quarter';
    completedBefore?: string;
    completedAfter?: string;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('reportType', options.reportType);
    params.append('periodUnit', options.periodUnit);
    if (options.completedBefore) params.append('completedBefore', options.completedBefore);
    if (options.completedAfter) params.append('completedAfter', options.completedAfter);
    return this.client.get<any[]>(`${this.basePath}/task/report?${params.toString()}`);
  }

  // ============================================================================
  // Historic Variable Instances
  // ============================================================================

  /**
   * Get historic variable instances
   */
  async getVariableInstances(query?: HistoricVariableInstanceQuery): Promise<HistoricVariableInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricVariableInstance[]>(`${this.basePath}/variable-instance${params}`);
  }

  /**
   * Get historic variable instances count
   */
  async getVariableInstancesCount(query?: HistoricVariableInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/variable-instance/count${params}`);
  }

  /**
   * Query historic variable instances (POST)
   */
  async queryVariableInstances(query: HistoricVariableInstanceQuery): Promise<HistoricVariableInstance[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<HistoricVariableInstance[]>(`${this.basePath}/variable-instance${params}`, body);
  }

  /**
   * Get historic variable instance by ID
   */
  async getVariableInstanceById(id: string): Promise<HistoricVariableInstance> {
    return this.client.get<HistoricVariableInstance>(`${this.basePath}/variable-instance/${id}`);
  }

  /**
   * Get historic variable instance binary data
   */
  async getVariableInstanceBinary(id: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/variable-instance/${id}/data`);
  }

  /**
   * Delete historic variable instances
   */
  async deleteVariableInstances(variableInstanceId: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/variable-instance/${variableInstanceId}`);
  }

  // ============================================================================
  // Historic Incidents
  // ============================================================================

  /**
   * Get historic incidents
   */
  async getIncidents(query?: HistoricIncidentQuery): Promise<HistoricIncident[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricIncident[]>(`${this.basePath}/incident${params}`);
  }

  /**
   * Get historic incidents count
   */
  async getIncidentsCount(query?: HistoricIncidentQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/incident/count${params}`);
  }

  /**
   * Get historic incident by ID
   */
  async getIncidentById(id: string): Promise<HistoricIncident> {
    return this.client.get<HistoricIncident>(`${this.basePath}/incident/${id}`);
  }

  // ============================================================================
  // Historic Job Logs
  // ============================================================================

  /**
   * Get historic job logs
   */
  async getJobLogs(query?: HistoricJobLogQuery): Promise<HistoricJobLog[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricJobLog[]>(`${this.basePath}/job-log${params}`);
  }

  /**
   * Get historic job logs count
   */
  async getJobLogsCount(query?: HistoricJobLogQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/job-log/count${params}`);
  }

  /**
   * Query historic job logs (POST)
   */
  async queryJobLogs(query: HistoricJobLogQuery): Promise<HistoricJobLog[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<HistoricJobLog[]>(`${this.basePath}/job-log${params}`, body);
  }

  /**
   * Get historic job log by ID
   */
  async getJobLogById(id: string): Promise<HistoricJobLog> {
    return this.client.get<HistoricJobLog>(`${this.basePath}/job-log/${id}`);
  }

  /**
   * Get historic job log stacktrace
   */
  async getJobLogStacktrace(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/job-log/${id}/stacktrace`);
  }

  // ============================================================================
  // Historic Decision Instances
  // ============================================================================

  /**
   * Get historic decision instances
   */
  async getDecisionInstances(query?: HistoricDecisionInstanceQuery): Promise<HistoricDecisionInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricDecisionInstance[]>(`${this.basePath}/decision-instance${params}`);
  }

  /**
   * Get historic decision instances count
   */
  async getDecisionInstancesCount(query?: HistoricDecisionInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/decision-instance/count${params}`);
  }

  /**
   * Get historic decision instance by ID
   */
  async getDecisionInstanceById(
    id: string,
    options?: {
      includeInputs?: boolean;
      includeOutputs?: boolean;
      disableBinaryFetching?: boolean;
      disableCustomObjectDeserialization?: boolean;
    }
  ): Promise<HistoricDecisionInstance> {
    const params = new URLSearchParams();
    if (options?.includeInputs) params.append('includeInputs', 'true');
    if (options?.includeOutputs) params.append('includeOutputs', 'true');
    if (options?.disableBinaryFetching) params.append('disableBinaryFetching', 'true');
    if (options?.disableCustomObjectDeserialization) params.append('disableCustomObjectDeserialization', 'true');
    const queryString = params.toString();
    return this.client.get<HistoricDecisionInstance>(
      `${this.basePath}/decision-instance/${id}${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Delete historic decision instances async
   */
  async deleteDecisionInstancesAsync(dto: {
    historicDecisionInstanceIds?: string[];
    historicDecisionInstanceQuery?: HistoricDecisionInstanceQuery;
    deleteReason?: string;
  }): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/decision-instance/delete`, dto);
  }

  /**
   * Set removal time for historic decision instances
   */
  async setDecisionInstanceRemovalTime(dto: {
    historicDecisionInstanceIds?: string[];
    historicDecisionInstanceQuery?: HistoricDecisionInstanceQuery;
    hierarchical?: boolean;
    absoluteRemovalTime?: string;
    clearedRemovalTime?: boolean;
    calculatedRemovalTime?: boolean;
  }): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/decision-instance/set-removal-time`, dto);
  }

  // ============================================================================
  // Historic Batches
  // ============================================================================

  /**
   * Get historic batches
   */
  async getBatches(query?: {
    batchId?: string;
    type?: string;
    completed?: boolean;
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<HistoricBatch[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<HistoricBatch[]>(`${this.basePath}/batch${params}`);
  }

  /**
   * Get historic batches count
   */
  async getBatchesCount(query?: {
    batchId?: string;
    type?: string;
    completed?: boolean;
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
  }): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/batch/count${params}`);
  }

  /**
   * Get historic batch by ID
   */
  async getBatchById(id: string): Promise<HistoricBatch> {
    return this.client.get<HistoricBatch>(`${this.basePath}/batch/${id}`);
  }

  /**
   * Delete historic batch
   */
  async deleteBatch(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/batch/${id}`);
  }

  /**
   * Set removal time for historic batches
   */
  async setBatchRemovalTime(dto: {
    historicBatchIds?: string[];
    historicBatchQuery?: any;
    absoluteRemovalTime?: string;
    clearedRemovalTime?: boolean;
    calculatedRemovalTime?: boolean;
  }): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/batch/set-removal-time`, dto);
  }

  // ============================================================================
  // Cleanable Reports
  // ============================================================================

  /**
   * Get cleanable historic process instance report
   */
  async getCleanableProcessInstanceReport(query?: {
    processDefinitionIdIn?: string[];
    processDefinitionKeyIn?: string[];
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    compact?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<CleanableHistoricProcessInstanceReport[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<CleanableHistoricProcessInstanceReport[]>(
      `${this.basePath}/process-instance/cleanable-process-instance-report${params}`
    );
  }

  /**
   * Get cleanable historic process instance report count
   */
  async getCleanableProcessInstanceReportCount(query?: {
    processDefinitionIdIn?: string[];
    processDefinitionKeyIn?: string[];
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    compact?: boolean;
  }): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(
      `${this.basePath}/process-instance/cleanable-process-instance-report/count${params}`
    );
  }

  // ============================================================================
  // Historic Details
  // ============================================================================

  /**
   * Get historic details
   */
  async getDetails(query?: {
    processInstanceId?: string;
    processInstanceIdIn?: string[];
    executionId?: string;
    taskId?: string;
    activityInstanceId?: string;
    caseInstanceId?: string;
    caseExecutionId?: string;
    variableInstanceId?: string;
    variableTypeIn?: string[];
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    userOperationId?: string;
    formFields?: boolean;
    variableUpdates?: boolean;
    excludeTaskDetails?: boolean;
    initial?: boolean;
    occurredBefore?: string;
    occurredAfter?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<any[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<any[]>(`${this.basePath}/detail${params}`);
  }

  /**
   * Get historic details count
   */
  async getDetailsCount(query?: {
    processInstanceId?: string;
    processInstanceIdIn?: string[];
    executionId?: string;
    taskId?: string;
    activityInstanceId?: string;
    caseInstanceId?: string;
    caseExecutionId?: string;
    variableInstanceId?: string;
    variableTypeIn?: string[];
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    userOperationId?: string;
    formFields?: boolean;
    variableUpdates?: boolean;
    excludeTaskDetails?: boolean;
    initial?: boolean;
    occurredBefore?: string;
    occurredAfter?: string;
  }): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/detail/count${params}`);
  }

  /**
   * Get historic detail by ID
   */
  async getDetailById(id: string, deserializeValue?: boolean): Promise<any> {
    const params = deserializeValue !== undefined ? `?deserializeValue=${deserializeValue}` : '';
    return this.client.get<any>(`${this.basePath}/detail/${id}${params}`);
  }

  /**
   * Get historic detail binary data
   */
  async getDetailBinary(id: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/detail/${id}/data`);
  }

  // ============================================================================
  // User Operations Log
  // ============================================================================

  /**
   * Get user operation logs
   */
  async getUserOperationLogs(query?: {
    deploymentId?: string;
    processDefinitionId?: string;
    processDefinitionKey?: string;
    processInstanceId?: string;
    executionId?: string;
    caseDefinitionId?: string;
    caseInstanceId?: string;
    caseExecutionId?: string;
    taskId?: string;
    externalTaskId?: string;
    batchId?: string;
    jobId?: string;
    jobDefinitionId?: string;
    userId?: string;
    operationId?: string;
    operationType?: string;
    entityType?: string;
    entityTypeIn?: string[];
    category?: string;
    categoryIn?: string[];
    property?: string;
    afterTimestamp?: string;
    beforeTimestamp?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<any[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<any[]>(`${this.basePath}/user-operation${params}`);
  }

  /**
   * Get user operation logs count
   */
  async getUserOperationLogsCount(query?: {
    deploymentId?: string;
    processDefinitionId?: string;
    processDefinitionKey?: string;
    processInstanceId?: string;
    executionId?: string;
    caseDefinitionId?: string;
    caseInstanceId?: string;
    caseExecutionId?: string;
    taskId?: string;
    externalTaskId?: string;
    batchId?: string;
    jobId?: string;
    jobDefinitionId?: string;
    userId?: string;
    operationId?: string;
    operationType?: string;
    entityType?: string;
    entityTypeIn?: string[];
    category?: string;
    categoryIn?: string[];
    property?: string;
    afterTimestamp?: string;
    beforeTimestamp?: string;
  }): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/user-operation/count${params}`);
  }

  /**
   * Set annotation for user operation log
   */
  async setUserOperationLogAnnotation(operationId: string, annotation: string | null): Promise<void> {
    await this.client.put<void>(`${this.basePath}/user-operation/${operationId}/set-annotation`, { annotation });
  }

  /**
   * Clear annotation for user operation log
   */
  async clearUserOperationLogAnnotation(operationId: string): Promise<void> {
    await this.client.put<void>(`${this.basePath}/user-operation/${operationId}/clear-annotation`, {});
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: Record<string, any>): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let historyServiceInstance: HistoryService | null = null;

export function getHistoryService(): HistoryService {
  if (!historyServiceInstance) {
    historyServiceInstance = new HistoryService();
  }
  return historyServiceInstance;
}

export default HistoryService;
